/**
 * BulkBcbaPanel — bulk set or clear the Overseeing BCBA (caseload owner) for the
 * selected clients.
 *
 * - assign: 80%-width drawer to pick ONE new BCBA. If some selected clients already
 *   have a BCBA, the user chooses Replace or Skip; on Replace they may also reassign
 *   those clients' open Red Dots to the new BCBA.
 * - unassign: a small confirm modal that clears the owner for all selected clients.
 */

import { useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { X, Check, Search, MapPin } from 'lucide-react';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { media } from '../../styles/media';
import {
  useAllAssignableProviders,
  useBulkBcbaPreview,
  useBulkUpdateBcba,
} from '../../hooks/useClients';
import type {
  AssignableProviderLite,
  BcbaConflictResolution,
  BulkBcbaResult,
} from '../../services/api/clientService';
import { formatPersonMeta, matchesPerson } from '../shared/PersonMeta';

export type BcbaMode = 'assign' | 'unassign';

interface BulkBcbaPanelProps {
  clientIds: string[];
  mode: BcbaMode;
  onClose: () => void;
  /** Called after a successful apply (parent clears selection + closes). */
  onDone: () => void;
}

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to   { transform: translateX(0); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: ${({ theme }) => theme.zIndex.modalBackdrop};
`;

const Panel = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(80vw, 1280px);
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-left: 1px solid ${({ theme }) => theme.colors.borders.light};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  z-index: ${({ theme }) => theme.zIndex.modal};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${slideIn} ${({ theme }) => theme.transitions.base} ease-out;

  ${media.desktop} {
    width: 90vw;
  }
  ${media.tablet} {
    width: 100vw;
  }
`;

const Header = styled.div`
  padding: ${({ theme }) => theme.spacing[5]} ${({ theme }) => theme.spacing[6]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const Title = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const Subtitle = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const CloseBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  flex-shrink: 0;
  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.gray};
    color: ${({ theme }) => theme.colors.text.primary};
  }
  svg {
    width: 18px;
    height: 18px;
  }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing[5]} ${({ theme }) => theme.spacing[6]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const SearchBox = styled.div`
  position: relative;
  svg {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.colors.text.light};
  }
  input {
    width: 100%;
    padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
    padding-left: 34px;
    border: 1px solid ${({ theme }) => theme.colors.borders.medium};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const ConflictBox = styled.div`
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.status.warning.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.warning.border};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const ConflictTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.status.warning.text};
`;

const Choice = styled.label`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
`;

const List = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing[2]};

  ${media.tablet} {
    grid-template-columns: 1fr;
  }
`;

const Row = styled.button<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  width: 100%;
  text-align: left;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border: 1px solid
    ${({ theme, $selected }) =>
      $selected ? theme.colors.primary.base : theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme, $selected }) =>
    $selected
      ? (theme.colors.primary.lightSky ?? theme.colors.backgrounds.light)
      : theme.colors.backgrounds.white};
  cursor: pointer;
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary.base};
  }
`;

const Radio = styled.span<{ $checked?: boolean }>`
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1.5px solid
    ${({ theme, $checked }) => ($checked ? theme.colors.primary.base : theme.colors.borders.medium)};
  background: ${({ theme, $checked }) => ($checked ? theme.colors.primary.base : 'transparent')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  svg {
    width: 11px;
    height: 11px;
  }
`;

const RowInfo = styled.div`
  flex: 1;
  min-width: 0;
  .name {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    font-weight: 500;
    color: ${({ theme }) => theme.colors.text.primary};
  }
  .meta {
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
    color: ${({ theme }) => theme.colors.text.secondary};
    display: inline-flex;
    align-items: center;
    gap: 4px;
    /* "Location · Role" can be long — wrap instead of widening the row. */
    overflow-wrap: anywhere;
    svg {
      flex-shrink: 0;
      width: 11px;
      height: 11px;
    }
  }
`;

const StateBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const EmptyNote = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

const ErrorText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.error.text};
  margin: 0;
`;

const ResultBox = styled.div`
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.status.success.bg};
  color: ${({ theme }) => theme.colors.status.success.text};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const Footer = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[6]};
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const ConfirmText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
`;

const fullName = (p: AssignableProviderLite) => `${p.firstName} ${p.lastName}`.trim();

const resultText = (r: BulkBcbaResult): string => {
  if (r.action === 'UNASSIGN') return `Removed the BCBA from ${r.cleared} client(s).`;
  const parts = [`Set BCBA on ${r.assigned} client(s)`];
  if (r.skipped > 0) parts.push(`${r.skipped} skipped`);
  if (r.redDotsTransferred > 0) parts.push(`${r.redDotsTransferred} Red Dot(s) reassigned`);
  return `${parts.join(' · ')}.`;
};

export function BulkBcbaPanel({ clientIds, mode, onClose, onDone }: BulkBcbaPanelProps) {
  const mutation = useBulkUpdateBcba();
  const [result, setResult] = useState<BulkBcbaResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const clientCount = clientIds.length;

  // ── Unassign: simple confirm modal ───────────────────────────────────────
  if (mode === 'unassign') {
    const submit = () => {
      setError(null);
      mutation.mutate(
        { clientIds, action: 'UNASSIGN' },
        {
          onSuccess: (r) => setResult(r),
          onError: () => setError('Failed to clear BCBA. Please try again.'),
        },
      );
    };
    return (
      <Modal
        isOpen
        onClose={onClose}
        title="Unassign Overseeing BCBA"
        footer={
          result ? (
            <Button variant="primary" onClick={onDone}>
              Done
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button variant="danger" isLoading={mutation.isPending} onClick={submit}>
                Remove BCBA
              </Button>
            </>
          )
        }
      >
        {result ? (
          <ResultBox>{resultText(result)}</ResultBox>
        ) : (
          <>
            <ConfirmText>
              Remove the Overseeing BCBA from {clientCount} selected client
              {clientCount === 1 ? '' : 's'}? Clients without a BCBA are unaffected; the previous
              BCBA keeps any regular assignment.
            </ConfirmText>
            {error && <ErrorText>{error}</ErrorText>}
          </>
        )}
      </Modal>
    );
  }

  // ── Assign: drawer with provider pick + conflict flow ─────────────────────
  return (
    <AssignBcbaDrawer
      clientIds={clientIds}
      onClose={onClose}
      onDone={onDone}
      result={result}
      setResult={setResult}
      error={error}
      setError={setError}
    />
  );
}

interface AssignDrawerProps {
  clientIds: string[];
  onClose: () => void;
  onDone: () => void;
  result: BulkBcbaResult | null;
  setResult: (r: BulkBcbaResult) => void;
  error: string | null;
  setError: (e: string | null) => void;
}

function AssignBcbaDrawer({
  clientIds,
  onClose,
  onDone,
  result,
  setResult,
  error,
  setError,
}: AssignDrawerProps) {
  const { data, isLoading, isError } = useAllAssignableProviders(true);
  const { data: preview } = useBulkBcbaPreview(clientIds, true);
  const mutation = useBulkUpdateBcba();

  const [search, setSearch] = useState('');
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [resolution, setResolution] = useState<BcbaConflictResolution | null>(null);
  const [transferRedDots, setTransferRedDots] = useState(false);

  const providers = useMemo(() => data ?? [], [data]);
  const conflictCount = preview?.withOwner.length ?? 0;
  const hasConflicts = conflictCount > 0;
  const clientCount = clientIds.length;

  // Matches name, email, location/center and role (ADO 398 + 406).
  const filtered = useMemo(
    () => providers.filter((p) => matchesPerson(p, search)),
    [providers, search],
  );

  const canSubmit = !!selectedMemberId && (!hasConflicts || resolution !== null);

  const submit = () => {
    if (!selectedMemberId) return;
    setError(null);
    mutation.mutate(
      {
        clientIds,
        action: 'ASSIGN',
        memberId: selectedMemberId,
        conflictResolution: hasConflicts ? (resolution ?? 'SKIP') : undefined,
        transferOpenRedDots: hasConflicts && resolution === 'REPLACE' ? transferRedDots : undefined,
      },
      {
        onSuccess: (r) => setResult(r),
        onError: () => setError('Failed to assign BCBA. Please try again.'),
      },
    );
  };

  return (
    <Overlay onClick={onClose}>
      <Panel onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <Header>
          <div>
            <Title>Assign Overseeing BCBA</Title>
            <Subtitle>
              to {clientCount} client{clientCount === 1 ? '' : 's'}
            </Subtitle>
          </div>
          <CloseBtn onClick={onClose} aria-label="Close">
            <X />
          </CloseBtn>
        </Header>

        <Body>
          {result ? (
            <ResultBox>{resultText(result)}</ResultBox>
          ) : (
            <>
              {hasConflicts && (
                <ConflictBox>
                  <ConflictTitle>
                    {conflictCount} of {clientCount} selected client
                    {clientCount === 1 ? '' : 's'} already have an Overseeing BCBA
                  </ConflictTitle>
                  <Choice>
                    <input
                      type="radio"
                      name="bcba-conflict"
                      checked={resolution === 'REPLACE'}
                      onChange={() => setResolution('REPLACE')}
                    />
                    Replace their current BCBA
                  </Choice>
                  {resolution === 'REPLACE' && (
                    <Choice style={{ marginLeft: 24 }}>
                      <input
                        type="checkbox"
                        checked={transferRedDots}
                        onChange={(e) => setTransferRedDots(e.target.checked)}
                      />
                      Also reassign their open Red Dots to the new BCBA
                    </Choice>
                  )}
                  <Choice>
                    <input
                      type="radio"
                      name="bcba-conflict"
                      checked={resolution === 'SKIP'}
                      onChange={() => setResolution('SKIP')}
                    />
                    Skip them (only set the BCBA on clients without one)
                  </Choice>
                </ConflictBox>
              )}

              <SearchBox>
                <Search />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search a BCBA by name, location, or role..."
                />
              </SearchBox>

              {isLoading && (
                <StateBox>
                  <LoadingSpinner size="sm" /> Loading providers...
                </StateBox>
              )}
              {isError && !isLoading && <ErrorText>Failed to load providers.</ErrorText>}
              {!isLoading && !isError && filtered.length === 0 && (
                <EmptyNote>No matching providers.</EmptyNote>
              )}

              <List>
                {filtered.map((p) => {
                  const selected = selectedMemberId === p.memberId;
                  return (
                    <Row
                      key={p.memberId}
                      type="button"
                      $selected={selected}
                      onClick={() => setSelectedMemberId(p.memberId)}
                    >
                      <Radio $checked={selected}>{selected && <Check />}</Radio>
                      <RowInfo>
                        <div className="name">{fullName(p)}</div>
                        <div className="meta">
                          <MapPin />{' '}
                          {formatPersonMeta({
                            centerName: p.centerName,
                            roleName: p.roleName,
                            locationFallback: 'No location',
                          })}
                        </div>
                      </RowInfo>
                    </Row>
                  );
                })}
              </List>

              {error && <ErrorText>{error}</ErrorText>}
            </>
          )}
        </Body>

        <Footer>
          {result ? (
            <Button variant="primary" onClick={onDone}>
              Done
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={!canSubmit}
                isLoading={mutation.isPending}
                onClick={submit}
              >
                Assign BCBA
              </Button>
            </>
          )}
        </Footer>
      </Panel>
    </Overlay>
  );
}

export default BulkBcbaPanel;
